import { useEffect, useRef } from 'react';
import BackButton from 'common/BackButton/BackButton';
import ErrorState from 'common/ErrorState/ErrorState';
import aboutImg from 'materials/img/about.png';
import clubTrainingImg from 'materials/img/catalogs/clubs.jpg';
import dancersImg from 'materials/img/catalogs/dancers.jpeg';
import { useContent } from 'hooks/useContent';
import { useRevealGroup } from 'hooks/useReveal';
import Loader from 'components/Loader/Loader';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { HistoryFacts, HistoryTimeline, HistoryPartners, HistoryFounders } from './About.blocks';
import './About.css';
import { useLanguage } from 'hooks/useLanguage';

gsap.registerPlugin(ScrollTrigger);

/**
 * Історія організації — вітринна сторінка руху (ADR-0011).
 *
 * Повний редизайн: замість одного фото, що висіло зліва весь час читання
 * (сенсу в ньому не читалося — ілюстрація без прив'язки до конкретного
 * місця тексту), сторінка тепер editorial-стрічка. Вільний текст лишається
 * таким, яким приходить із бекенду (`history.text`, розбивка на абзаци —
 * структурна, за розривами рядків), а реальні факти організації — дати,
 * турніри, договори, членства — структуровані дані в history.data.js,
 * бо таблицю з десятків рядків текстовим полем CMS не описати розумно.
 *
 * BLOCKS_AFTER — той самий принцип, що раніше був у PHOTOS_AFTER: прив'язка
 * до номера абзаца, а не до CMS. Абзац 0 завжди відкриває сторінку
 * (теза-маніфест), останній — завжди закриває (той самий стиль, дзеркально):
 * це перевіряється через довжину масиву, а не хардкод індексу, тож зміна
 * кількості абзаців не ламає розкладку. Середні індекси (2, 3, 5, 6, 7)
 * прив'язані жорстко — якщо текст сильно зміниться, блоки просто спливуть
 * в іншому місці, сторінка не зламається.
 */
const BLOCKS_AFTER = {
  0: [
    { type: 'photo', key: 'cover', src: aboutImg, altUa: 'Танцювальна пара виступає на паркеті під час змагань', altEn: 'A dancesport couple performing on the floor during a competition' },
    { type: 'facts', key: 'facts' },
  ],
  2: [
    { type: 'photo', key: 'clubs', src: clubTrainingImg, altUa: 'Діти тренуються в танцювальному класі перед дзеркалом', altEn: 'Children training in a dance class in front of a mirror' },
  ],
  3: [{ type: 'timeline', key: 'timeline' }],
  5: [{ type: 'partners', key: 'partners' }],
  6: [{ type: 'founders', key: 'founders' }],
  7: [
    { type: 'photo', key: 'dancers', src: dancersImg, altUa: 'Танцювальна пара в динамічній позі бального танцю', altEn: 'A dancesport couple in a dynamic ballroom pose' },
  ],
};

export default function About() {
  const language = useLanguage();
  const { data: history, status, error, retry, isLoading } = useContent('history');

  const text = history ? history.text : '';
  const paragraphs = splitIntoParagraphs(text);

  const bodyRef = useRef(null);
  const textGroupRef = useRevealGroup('.history-paragraph, .history-photo', { step: 80, threshold: 0.15 }, status);

  // Паралакс-скраб фото — масштаб і легкий поворот, кожне окремо за власним
  // тригером (а не одним спільним на весь блок тексту, як було при старій
  // двоколонковій розкладці з єдиним липким фото) — ADR-0011.
  useEffect(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced || status !== 'succeeded' || !bodyRef.current) return undefined;

    const ctx = gsap.context(() => {
      const photos = bodyRef.current.querySelectorAll('.history-photo img');
      photos.forEach((img, index) => {
        gsap.fromTo(
          img,
          { scale: 1, rotate: index % 2 === 0 ? -1.6 : 1.6 },
          {
            scale: 1.14,
            rotate: 0,
            ease: 'none',
            scrollTrigger: {
              trigger: img.closest('.history-photo'),
              start: 'top bottom',
              end: 'bottom top',
              scrub: 1,
            },
          }
        );
      });
    }, bodyRef);

    return () => ctx.revert();
  }, [status]);

  function renderBlocks(index) {
    const blocks = BLOCKS_AFTER[index];
    if (!blocks) return [];

    return blocks.map((block) => {
      switch (block.type) {
        case 'photo':
          return (
            <figure className='history-photo' key={block.key}>
              <img src={block.src} alt={language === 'en' ? block.altEn : block.altUa} loading='lazy' />
            </figure>
          );
        case 'facts':
          return <HistoryFacts language={language} key={block.key} />;
        case 'timeline':
          return <HistoryTimeline language={language} key={block.key} />;
        case 'partners':
          return <HistoryPartners language={language} key={block.key} />;
        case 'founders':
          return <HistoryFounders language={language} key={block.key} />;
        default:
          return null;
      }
    });
  }

  return (
    <article className='about-page history-page section'>
      <div className='history-grain' aria-hidden='true' />

      <div className='container'>
        <BackButton />

        <header className='history-hero'>
          <h1 className='history-title'>
            <span className='history-title-mask'>
              <span className='history-title-inner'>
                {language === 'en' ? 'About the organization' : 'Про організацію'}
              </span>
            </span>
          </h1>
        </header>

        {isLoading && <Loader />}

        {status === 'failed' && <ErrorState error={error} onRetry={retry} />}

        {status === 'succeeded' &&
          (paragraphs.length > 0 ? (
            <div className='history-body' ref={bodyRef}>
              <h2 className='history-subtitle'>{language === 'en' ? 'History' : 'Історія'}</h2>

              <div className='history-text' ref={textGroupRef}>
                {paragraphs.map((paragraph, index) => {
                  const isOpening = index === 0;
                  const isClosing = index === paragraphs.length - 1;

                  // Масив, а не Fragment: ключ потрібен на самому елементі
                  // списку, а <>...</> його не приймає.
                  return [
                    <p
                      key={paragraph.slice(0, 24) + index}
                      className={`history-paragraph ${isOpening || isClosing ? 'history-statement' : ''} ${
                        isClosing ? 'history-statement--closing' : ''
                      }`}
                    >
                      {paragraph}
                    </p>,
                    ...renderBlocks(index),
                  ];
                })}
              </div>
            </div>
          ) : (
            <p className='empty-note'>
              {language === 'en' ? 'The history has not been filled in yet.' : 'Історію ще не заповнено.'}
            </p>
          ))}
      </div>
    </article>
  );
}

/**
 * Подвійний розрив рядка як параграф; якщо в тексті такого нема, розбиваємо
 * за одинарними розривами — CMS-текст форматується довільно, і жодного з
 * варіантів гарантовано нема.
 */
function splitIntoParagraphs(text) {
  if (!text) return [];

  const byDoubleBreak = text
    .split(/\n{2,}/)
    .map((part) => part.trim())
    .filter(Boolean);

  if (byDoubleBreak.length > 1) return byDoubleBreak;

  const bySingleBreak = text
    .split(/\n+/)
    .map((part) => part.trim())
    .filter(Boolean);

  return bySingleBreak.length > 0 ? bySingleBreak : [text.trim()];
}
